import { useEffect, useLayoutEffect, useRef } from 'react';
import { ChevronDown, Gear } from 'react-bootstrap-icons';
import { useLocation } from 'react-router-dom';

import { DataTestIds } from '@repo/frontend-shared';
import { SDSClickAway } from '@repo/sds/SDSClickAway';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSIcon } from '@repo/sds/SDSIcon';
import { SDSPrimitiveButton } from '@repo/sds/SDSPrimitiveButton';
import { SDSText } from '@repo/sds/SDSText';
import type { SettingsPanelItemProps } from '@repo/ui/components/elements/SettingsPanelItem/SettingsPanelItem';
import { SettingsPanelItem } from '@repo/ui/components/elements/SettingsPanelItem/SettingsPanelItem';
import { SETTINGS_MOBILE_MENU_DROPDOWN_ID, useCoordinatedDropdown } from '@repo/ui/hooks/use-coordinated-dropdown';

import './settings-mobile-menu.scss';

const SETTINGS_MOBILE_MENU_ID = 'settings-mobile-menu';
const DROPDOWN_GAP_PX = 4;
const VIEWPORT_BOTTOM_PADDING_PX = 8;

interface SettingsMobileMenuProps {
  items: SettingsPanelItemProps[];
  allItems: SettingsPanelItemProps[];
}

/**
 * Mobile settings navigation trigger with dropdown list of settings sections.
 */
export function SettingsMobileMenu({ items, allItems }: SettingsMobileMenuProps) {
  const { isOpen, close, toggle } = useCoordinatedDropdown(SETTINGS_MOBILE_MENU_DROPDOWN_ID);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLElement>(null);
  const location = useLocation();

  const activeItem = allItems.find(item => location.pathname.startsWith(item.path));
  const ActiveIcon = activeItem?.icon ?? Gear;
  const activeTitle = activeItem?.title ?? 'Settings';

  useLayoutEffect(() => {
    if (!isOpen) return;
    const updateAvailableHeight = () => {
      const trigger = triggerRef.current;
      const dropdown = dropdownRef.current;
      if (!(trigger && dropdown)) return;
      const viewport = window.visualViewport;
      const viewportBottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
      const availableHeight = Math.max(
        0,
        viewportBottom - trigger.getBoundingClientRect().bottom - DROPDOWN_GAP_PX - VIEWPORT_BOTTOM_PADDING_PX,
      );
      dropdown.style.setProperty('--settings-menu-available-height', `${availableHeight}px`);
    };
    updateAvailableHeight();
    window.addEventListener('resize', updateAvailableHeight);
    window.addEventListener('scroll', updateAvailableHeight, true);
    window.visualViewport?.addEventListener('resize', updateAvailableHeight);
    return () => {
      window.removeEventListener('resize', updateAvailableHeight);
      window.removeEventListener('scroll', updateAvailableHeight, true);
      window.visualViewport?.removeEventListener('resize', updateAvailableHeight);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      close();
      triggerRef.current?.focus();
    };

    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => document.removeEventListener('keydown', handleDocumentKeyDown);
  }, [close, isOpen]);

  return (
    <SDSClickAway onClickAway={close} className="SettingsMobileMenu">
      <SDSPrimitiveButton
        type="button"
        className="SettingsMobileMenu__trigger"
        dataTestId={DataTestIds.COMMON.SETTING_PANEL.MOBILE_TRIGGER}
        buttonRef={triggerRef}
        ariaLabel={`${activeTitle}, ${isOpen ? 'close' : 'open'} settings navigation`}
        ariaExpanded={isOpen}
        ariaControls={SETTINGS_MOBILE_MENU_ID}
        onClick={toggle}
      >
        <SDSIcon className="SettingsMobileMenu__triggerIcon" icon={<ActiveIcon />} size="medium" color="secondary" />
        <SDSText text={activeTitle} type="body-large-bold" color="primary" isInline />
        <SDSIcon
          className={
            isOpen ? 'SettingsMobileMenu__chevron SettingsMobileMenu__chevron--open' : 'SettingsMobileMenu__chevron'
          }
          icon={<ChevronDown />}
          size="small"
          color="secondary"
        />
      </SDSPrimitiveButton>

      {isOpen && (
        <nav
          ref={dropdownRef}
          id={SETTINGS_MOBILE_MENU_ID}
          className="SettingsMobileMenu__dropdown"
          aria-label="Settings"
          data-testid={DataTestIds.COMMON.SETTING_PANEL.MOBILE_DROPDOWN}
        >
          <SDSFlexbox direction="column" fullWidth>
            {items.map(item => (
              <SettingsPanelItem key={item.path} {...item} onNavigate={close} variant="dropdown" />
            ))}
          </SDSFlexbox>
        </nav>
      )}
    </SDSClickAway>
  );
}
